// FP 간트 — 작업 상세 정보 패널(우클릭 → 메뉴 → 상세)
//
// old(TreeGrid)는 `OnRightClickGantt: 'GanttMenu,1'`로 항목 1개짜리 메뉴를 띄우고, 클릭하면
// `resource-gantt/activity/detail`을 호출해 결과를 **TreeGrid 내장 details 패널**에 넘겼다.
// new에는 그 패널이 없어 여기서 만든다. 설계·시안 비교는 reports/gantt-implementation.md §7-7,
// prototypes/gantt-detail-panel.html.
//
// **표시 방식 = 오른쪽 오버레이(A안, 2026-08-21 사용자 확정).** MUI `Drawer`를 쓰지 않은 이유:
//   - `variant="persistent"`는 열릴 때 간트 폭이 줄어든다. 자체 프레임은 캔버스를 뷰포트 크기로 다시
//     만들고 스케일·눈금을 재계산하므로 **열고 닫을 때마다 전체 재렌더 + 바 위치 이동**이 생긴다.
//     상세를 보며 다음 바를 찍는 이 화면에서는 보고 있던 바를 놓친다.
//   - `variant="temporary"`는 backdrop이 간트를 덮어 **다른 바를 누를 수 없다** — 사용 흐름과 정면 충돌.
//   앱 선례도 `ChatWidget`·`AiScenarioPlanning`이 같은 계열(고정 우측 패널)이다.
//
// 행 표기(2열 정렬·글자 3단계·WIP 체크박스·지연 강조)는 `GanttDetailRows`를 공유해 툴팁과 갈라지지 않게 한다.

import { useEffect } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";

import GanttDetailRows, { DETAIL_GRID } from "@plannel/pages/factory-plan/common/gantt/GanttDetailRows";
import { UI } from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

export const DETAIL_PANEL_WIDTH = 340;

// 라벨 열 최소 폭 — 이 값보다 긴 라벨이 있는 섹션만 그만큼 더 넓어진다(minmax의 상한이 max-content).
// 한국어 최장 라벨("작업 주문 코드"/"판매 주문 수량" 7자 ≈ 88px)과 영문("Work Order Code" ≈ 105px)이
// 모두 들어가는 값이라, 실제 데이터에서는 두 섹션이 같은 폭으로 떨어진다.
const LABEL_COL_MIN_WIDTH = 120;

const SectionHeader = ({ titleKey, open, onToggle, divided, t }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none",
      px: 1.75, py: 1, backgroundColor: "#f7f9fa", borderBottom: "1px solid", borderColor: "divider",
      // 앞 섹션의 마지막 행과 맞붙지 않도록 위쪽에도 선을 긋는다. 첫 섹션은 패널 헤더 바로 아래라
      // 그 헤더의 아래 선과 겹치므로 주지 않는다.
      ...(divided ? { borderTop: "1px solid", borderTopColor: "divider" } : null),
      // 접을 수 있다는 신호 — 셰브론만으로는 회색 밴드에 묻혀 눈에 띄지 않는다.
      transition: "background-color 120ms ease-out",
      "&:hover": { backgroundColor: "#eef2f4" }
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.headerFg }}>{t(titleKey)}</Typography>
    <Box sx={{ flex: "1 1 auto" }} />
    <ExpandMoreIcon
      sx={{
        // 색을 한 단 진하게(dim → 본문색) — dim은 배경 대비가 너무 낮아 화살표가 안 보였다.
        fontSize: 18, color: UI.headerFg,
        transform: open ? "none" : "rotate(-90deg)", transition: "transform 150ms ease-out"
      }}
    />
  </Box>
);

/**
 * sections: [{ key, titleKey, rows: [{ labelKey, value, wrap?, checkbox?, tone? }] }]
 * openSections: Set<string> — 열린 섹션 키. 접기 상태를 화면이 소유한다(탭 전환 시 유지 가능).
 * loading: 상세 조회 중. loadingMessage는 화면이 번역한 공통 키(`MSG_Loading`)를 넘긴다.
 */
const GanttDetailPanel = ({
  open, sections = [], openSections, onToggleSection, onClose, loading = false, loadingMessage = "", t
}) => {
  // Esc로 닫는다. 닫아도 **선택은 유지**한다 — 어느 바를 보던 중이었는지 남아야 한다(화면이 소유).
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // 나타남·사라짐 모두 애니메이션이 필요하다 → `open`으로 조건부 언마운트하지 않고 `Slide`에 맡긴다
  // (`if (!open) return null`이면 **닫힐 때 그냥 사라진다**). `unmountOnExit`으로 닫힌 뒤에는 DOM에서
  // 빠져 캔버스 위에 투명 레이어가 남지 않는다.
  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit timeout={{ enter: 220, exit: 180 }}>
      <Paper
        elevation={0}
        sx={{
          position: "absolute", top: 0, right: 0, bottom: 0, width: DETAIL_PANEL_WIDTH, zIndex: 4,
          display: "flex", flexDirection: "column",
          borderLeft: "1px solid", borderColor: "divider", borderRadius: 0,
          boxShadow: "-6px 0 18px rgba(15, 23, 42, 0.08)"
        }}
      >
        {/* 헤더 — 툴팁 밴드와 같은 틴트를 써서 "같은 상세 정보"임을 색으로 잇는다. */}
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 1, flex: "0 0 auto",
            px: 1.75, py: 1.25, backgroundColor: UI.tipBandBg,
            borderBottom: "1px solid", borderColor: "divider"
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI.tipValueFg }}>
            {t("FP_ACTIVITY_DETAIL")}
          </Typography>
          <Box sx={{ flex: "1 1 auto" }} />
          <IconButton size="small" onClick={onClose} aria-label={t("close")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ position: "relative", flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
          {/* 로딩 표시는 **스피너 + 문구 하나**로만 한다. 헤더에 흐르는 진행 바(LinearProgress)는
              2026-08-21 사용자 요청으로 제거했다 — 패널이 좁아 움직이는 선이 계속 시야에 걸린다.
              **절대배치 오버레이**라 나타나고 사라질 때 본문이 밀리지 않는다. 값이 이미 있으면 아래
              본문을 흐리게만 하고 지우지 않는다(지우면 바를 옮길 때마다 비었다 채워져 깜빡인다).
              문구는 공통 키 `MSG_Loading`. */}
          {loading ? (
            <Box
              sx={{
                position: "absolute", top: 0, left: 0, right: 0,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25,
                py: 6, zIndex: 1, pointerEvents: "none"
              }}
            >
              <CircularProgress size={18} thickness={4.5} sx={{ color: UI.headerAccent }} />
              <Typography variant="caption" sx={{ color: UI.cellFg }}>{loadingMessage}</Typography>
            </Box>
          ) : null}

          <Box
            sx={{
              transition: "opacity 150ms ease-out",
              ...(loading && sections.length > 0 ? { opacity: 0.45, pointerEvents: "none" } : null)
            }}
          >
            {sections.map((section, index) => {
              const sectionOpen = openSections ? openSections.has(section.key) : true;

              return (
                <Box key={section.key}>
                  <SectionHeader
                    titleKey={section.titleKey}
                    open={sectionOpen}
                    onToggle={() => onToggleSection?.(section.key)}
                    divided={index > 0}
                    t={t}
                  />
                  <Collapse in={sectionOpen} timeout={150} unmountOnExit>
                    {/* 값이 길면(작업 주문 코드·공정 코드) 두 줄로 흘린다 — 패널은 폭이 고정이라
                        툴팁처럼 `nowrap`으로 두면 잘린다.

                        ⚠️ **라벨 열은 최소 폭을 고정한다**(`LABEL_COL_MIN_WIDTH`). 섹션마다 grid가
                        따로라 `max-content`만 쓰면 라벨 열 폭이 **섹션별로 따로 계산**되어, 짧은 라벨만
                        있는 "작업 정보"와 긴 라벨이 있는 "주문 정보"의 값 열 시작 위치가 어긋난다
                        (2026-08-25 사용자 지적). 최소 폭을 주면 두 섹션이 같은 자리에서 시작하고,
                        라벨-값 간격도 함께 넓어진다. 툴팁은 폭이 내용에 맞춰 늘어나야 하므로
                        `DETAIL_GRID` 자체는 건드리지 않고 **패널에서만** 덮는다. */}
                    <Box
                      sx={{
                        ...DETAIL_GRID,
                        gridTemplateColumns: `minmax(${LABEL_COL_MIN_WIDTH}px, max-content) 1fr`,
                        // 행 간격 6px — 3px에서는 20행이 한 덩어리로 붙어 답답하다(2026-08-25 사용자 확인).
                        // 툴팁(DETAIL_GRID 기본 2px)은 그대로 둔다. 떠 있는 동안만 보는 것이라 조밀한 게 맞다.
                        px: 1.75, py: 1.25, rowGap: 0.75
                      }}
                    >
                      <GanttDetailRows rows={section.rows.map(row => ({ wrap: true, ...row }))} t={t} />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default GanttDetailPanel;
